import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from "react";
import { playWhooshSound } from "@/utils/audioSynth";

interface SoundContextType {
  isMuted: boolean;
  volume: number;
  toggleMute: () => void;
  setVolume: (volume: number) => void;
  playTransitionSound: (type?: "enter" | "exit") => void;
}

const SoundContext = createContext<SoundContextType | undefined>(undefined);

export const useSoundContext = () => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error("useSoundContext must be used within a SoundProvider");
  }
  return context;
};

interface SoundProviderProps {
  children: ReactNode;
}

export const SoundProvider = ({ children }: SoundProviderProps) => {
  const [isMuted, setIsMuted] = useState(() => {
    const saved = localStorage.getItem("soundMuted");
    return saved === "true";
  });
  
  const [volume, setVolumeState] = useState(() => {
    const saved = localStorage.getItem("soundVolume");
    return saved ? parseFloat(saved) : 0.5;
  });

  useEffect(() => {
    localStorage.setItem("soundMuted", String(isMuted));
  }, [isMuted]);

  useEffect(() => {
    localStorage.setItem("soundVolume", String(volume));
  }, [volume]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(Math.max(0, Math.min(1, newVolume)));
  }, []);

  const playTransitionSound = useCallback((type: "enter" | "exit" = "enter") => {
    if (isMuted) return;
    
    try {
      playWhooshSound(type, volume);
    } catch (error) {
      console.log("Could not play transition sound:", error);
    }
  }, [isMuted, volume]);

  return (
    <SoundContext.Provider
      value={{
        isMuted,
        volume,
        toggleMute,
        setVolume,
        playTransitionSound,
      }}
    >
      {children}
    </SoundContext.Provider>
  );
};
